import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useStats } from '@/contexts/StatsContext';
import {
  Clock,
  Heart,
  Baby,
  User,
  RefreshCw,
  Zap,
  AlertCircle,
  ArrowLeft,
  Crown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const ActionDetail = () => {
  const { t, i18n } = useTranslation(['translation', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isPremium } = useUser();
  const { refreshStats } = useStats();

  const suggestion = location.state?.suggestion;
  const userId = user?.user_id || user?.id;

  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [requestingNew, setRequestingNew] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState(5);

  const triggerConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:9999;background:transparent;';
    document.body.appendChild(canvas);
    const fire = confetti.create(canvas, { resize: true });

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) { clearInterval(interval); setTimeout(() => canvas.remove(), 500); return; }

      const particleCount = 50 * (timeLeft / duration);
      fire({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      fire({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  };

  const getRandomEncouragement = () => {
    const messages = t('dashboard.congratulations', { returnObjects: true });
    return messages[Math.floor(Math.random() * messages.length)] || 'Great job!';
  };

  const handleFeedback = async (status) => {
    if (!suggestion) return;

    if (status === 'done') {
      setShowNotes(true);
      return;
    }

    try {
      await axios.post(`${API}/suggestion/feedback`, { suggestion_id: suggestion.suggestion_id, status });
      toast.success(t('dashboard.feedbackSuccess'));
      navigate('/dashboard');
      refreshStats();
    } catch (e) {
      console.error('[ActionDetail] feedback error:', e.response?.data || e);
      toast.error(i18n.language === 'it' ? 'Errore nel salvataggio del feedback' : i18n.language === 'es' ? 'Error al guardar el feedback' : 'Error saving feedback');
    }
  };

  const handleSaveWithNotes = async () => {
    if (!suggestion) return;

    try {
      await axios.post(
        `${API}/suggestion/feedback`,
        {
          suggestion_id: suggestion.suggestion_id,
          status: 'done',
          notes: notes.trim() || null
        },
        []
      );

      triggerConfetti();
      toast.success(getRandomEncouragement(), { duration: 4000 });

      setSuggestion({ ...suggestion, status: 'done', notes: notes.trim() });
      setShowNotes(false);
      setNotes('');
      navigate('/dashboard');
      refreshStats();
    } catch (e) {
      console.error('[ActionDetail] save notes error:', e.response?.data || e);
      toast.error(i18n.language === 'it' ? 'Errore nel salvataggio del feedback' : i18n.language === 'es' ? 'Error al guardar el feedback' : 'Error saving feedback');
    }
  };

  const requestNewSuggestion = useCallback(async () => {
    if (!userId || !isPremium) return;
    try {
      setRequestingNew(true);
      const res = await axios.post(`${API}/suggestion/generate-new/${userId}`);
      const newSuggestion = { ...res.data, status: 'pending' };
      if (res.data.remaining_requests !== undefined) {
        setRemainingRequests(res.data.remaining_requests);
      }
      navigate('/dashboard');
    } catch (e) {
      console.error('[ActionDetail] requestNewSuggestion error:', e.response?.data || e);
      const errorMsg =
        e.response?.data?.detail ||
        (i18n.language === 'it' ? 'Errore nella generazione' : i18n.language === 'es' ? 'Error en la generación' : 'Generation error');
      toast.error(errorMsg);
    } finally {
      setRequestingNew(false);
    }
  }, [userId, isPremium, navigate, i18n.language]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'husband':
        return <Heart className="w-5 h-5" />;
      case 'father':
        return <Baby className="w-5 h-5" />;
      case 'partner':
        return <User className="w-5 h-5" />;
      default:
        return <Heart className="w-5 h-5" />;
    }
  };

  const getContextLabel = (context) => {
    const contextMap = {
      morning: i18n.language === 'it' ? (<><span role="img" aria-label="Mattina">🌅</span> Mattina</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Mañana">🌅</span> Mañana</>) : (<><span role="img" aria-label="Morning">🌅</span> Morning</>),
      afternoon: i18n.language === 'it' ? (<><span role="img" aria-label="Pomeriggio">☀️</span> Pomeriggio</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Tarde">☀️</span> Tarde</>) : (<><span role="img" aria-label="Afternoon">☀️</span> Afternoon</>),
      evening: i18n.language === 'it' ? (<><span role="img" aria-label="Sera">🌙</span> Sera</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Noche">🌙</span> Noche</>) : (<><span role="img" aria-label="Evening">🌙</span> Evening</>),
      anytime: i18n.language === 'it' ? (<><Clock className="inline w-4 h-4 mr-1 align-text-bottom" />Quando vuoi</>) : i18n.language === 'es' ? (<><Clock className="inline w-4 h-4 mr-1 align-text-bottom" />Cuando quieras</>) : (<><Clock className="inline w-4 h-4 mr-1 align-text-bottom" />Anytime</>),
      weekend: i18n.language === 'it' ? (<><span role="img" aria-label="Weekend">🛋️</span> Nel weekend</>) : i18n.language === 'es' ? (<><span role="img" aria-label="Fin de semana">🛋️</span> El fin de semana</>) : (<><span role="img" aria-label="Weekend">🛋️</span> On the weekend</>),
    };
    return contextMap[context] || String(context || '').replace(/_/g, ' ');
  };

  const getTimeIndicator = (context) => {
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
    if (context === 'anytime') return null;
    return context === timeOfDay;
  };

  if (!suggestion) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container">
        <div className="text-center p-8">
          <p style={{ color: 'var(--c-texSec)' }}>
            {i18n.language === 'it' ? 'Suggerimento non trovato' : i18n.language === 'es' ? 'Sugerencia no encontrada' : 'Suggestion not found'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-3 rounded-full font-medium"
            style={{ backgroundColor: 'var(--c-texPri)', color: 'var(--c-texDis)' }}
          >
            {i18n.language === 'it' ? 'Torna alla dashboard' : i18n.language === 'es' ? 'Volver al panel' : 'Back to dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAF9] pb-safe safe-area-top" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <div className="p-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{i18n.language === 'it' ? 'Indietro' : i18n.language === 'es' ? 'Atrás' : 'Back'}</span>
          </button>
        </div>

        {/* Suggestion Detail */}
        <div className="px-6 pb-8">
          <div className="rounded-xl p-8 flex flex-col gap-6 border bg-white text-black border-stone-200">
            <div className="pb-4 border-b border-stone-200">
              <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-stone-600">
                {i18n.language === 'it' ? 'Suggerimento del giorno' : i18n.language === 'es' ? 'Sugerencia del día' : "Today's suggestion"}
              </p>
              <div className="flex flex-col items-start w-full">
                <h2 className="font-serif text-2xl leading-tight mb-0 w-full break-words text-black">
                  {suggestion.main_suggestion.text}
                </h2>
                {suggestion.main_suggestion.context && (
                  <span
                    className={`mt-2 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap align-middle ${
                      getTimeIndicator(suggestion.main_suggestion.context)
                        ? 'bg-amber-100 text-amber-700 border border-amber-300'
                        : 'bg-stone-100 text-stone-600'
                    }`}
                  >
                    {getContextLabel(suggestion.main_suggestion.context)}
                  </span>
                )}
              </div>
            </div>

            {suggestion.main_suggestion.description && (
              <p className="text-base leading-relaxed mt-2 mb-4 text-black" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                {suggestion.main_suggestion.description}
              </p>
            )}

            <div className="flex flex-wrap gap-4 text-sm pt-2 text-black">
              {suggestion.main_suggestion.estimated_time && (
                <div className="flex items-center gap-2 text-black">
                  <Clock className="w-4 h-4" />
                  <span>
                    {suggestion.main_suggestion.estimated_time} {i18n.language === 'it' ? 'min' : i18n.language === 'es' ? 'min' : 'min'}
                  </span>
                </div>
              )}
              {suggestion.main_suggestion.difficulty && (
                <div className="flex items-center gap-2 text-black">
                  <Zap className="w-4 h-4" />
                  <span>{mapDifficulty(suggestion.main_suggestion.difficulty, i18n.language)}</span>
                </div>
              )}
              {suggestion.main_suggestion.category && (
                <div className="flex items-center gap-2 text-black">
                  {getCategoryIcon(suggestion.main_suggestion.category)}
                  <span className="capitalize">{mapCategory(suggestion.main_suggestion.category, i18n.language)}</span>
                </div>
              )}
            </div>

            {suggestion.main_suggestion.addresses_tension && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-black">
                  <p className="font-medium mb-1">{i18n.language === 'it' ? 'Affronta' : i18n.language === 'es' ? 'Aborda' : 'Addresses'}</p>
                  <p className="text-black">{suggestion.main_suggestion.addresses_tension}</p>
                </div>
              </div>
            )}

            {(!suggestion.status || suggestion.status === 'pending') && (
              <div className="flex flex-col gap-4 pt-4">
                <button
                  onClick={() => handleFeedback('done')}
                  className="w-full h-12 rounded-full font-medium bg-black text-white hover:bg-stone-800 active:scale-95 transition-all active:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('dashboard.done')}
                </button>
                <button
                  onClick={() => handleFeedback('postponed')}
                  className="w-full h-12 font-medium bg-inherit text-[#a8a29e] underline hover:text-stone-700 active:scale-95 transition-all active:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed border-none"
                >
                  {t('dashboard.postponed')}
                </button>
                <button
                  onClick={requestNewSuggestion}
                  disabled={requestingNew || suggestion.status === 'done' || (!isPremium && remainingRequests <= 0)}
                  className="btn-hide-icon w-full px-4 py-3 rounded-full text-base font-medium flex items-center gap-3 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-stone-100 text-stone-900 hover:bg-stone-200"
                  style={{ justifyContent: 'flex-start', textAlign: 'left', boxShadow: '0 2px 8px 0 rgba(0,0,0,0.07)', fontFamily: 'Public Sans, sans-serif', opacity: suggestion.status === 'done' ? 0.5 : 1 }}
                >
                  <RefreshCw className={`w-5 h-5 mr-3 ${requestingNew ? 'animate-spin' : ''}`} />
                  <div className="flex flex-col flex-1">
                    <span className="text-left font-medium text-stone-900">
                      {i18n.language === 'it' ? "Provane un'altra" : i18n.language === 'es' ? "Prueba otra" : "Try another"}
                    </span>
                    {!isPremium && (
                      <span className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <Crown className="w-4 h-4 inline-block" />
                        {i18n.language === 'it' ? 'Solo per utenti Premium' : i18n.language === 'es' ? 'Solo para usuarios Premium' : 'Premium only'}
                      </span>
                    )}
                  </div>
                  {isPremium && (
                    <span className="text-sm font-semibold ml-auto text-stone-900">({remainingRequests}/5)</span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 space-y-6 animate-in fade-in zoom-in-95">
            <div>
              <h2 className="font-serif text-2xl text-stone-900 mb-2">{t('dashboard.notesTitle') || 'Note'}</h2>
              <p className="text-sm text-stone-500">
                {t('dashboard.notesSubtitle') ||
                  (i18n.language === 'it' ? 'Aggiungi note su come è andata' : i18n.language === 'es' ? 'Agrega notas sobre cómo te fue' : 'Add notes about how it went')}
              </p>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('dashboard.notesPlaceholder') || (i18n.language === 'it' ? 'Scrivi una nota...' : i18n.language === 'es' ? 'Escribe una nota...' : 'Write a note...')}
              className="w-full h-32 p-4 border border-stone-200 rounded-full focus:border-stone-900 focus:ring-0 resize-none"
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowNotes(false);
                  setNotes('');
                }}
                className="flex-1 h-12 rounded-full border border-stone-200 text-stone-900 hover:bg-stone-50 transition-all"
              >
                {i18n.language === 'it' ? 'Annulla' : i18n.language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>

              <button
                onClick={handleSaveWithNotes}
                className="flex-1 h-12 rounded-full bg-stone-900 text-white hover:bg-stone-800 transition-all"
              >
                {i18n.language === 'it' ? 'Salva' : i18n.language === 'es' ? 'Guardar' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionDetail;

function mapDifficulty(diff, language) {
  switch (diff) {
    case 'low':
    case 'Facile':
      return language === 'it' ? 'Facile' : 'Easy';
    case 'medium':
    case 'Medio':
      return language === 'it' ? 'Medio' : 'Medium';
    case 'high':
    case 'Difficile':
      return language === 'it' ? 'Difficile' : 'Hard';
    default:
      return diff;
  }
}

function mapCategory(cat, language) {
  switch (cat) {
    case 'husband':
    case 'Marito':
      return language === 'it' ? 'Marito' : 'Husband';
    case 'father':
    case 'Padre':
      return language === 'it' ? 'Padre' : 'Father';
    case 'partner':
    case 'Partner':
      return language === 'it' ? 'Partner' : 'Partner';
    default:
      return cat;
  }
}